import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert as JoyAlert, CircularProgress } from '@mui/joy';
import type { StormAlert } from '../../types/stormtracker';

interface AlertMapProps {
  alert: StormAlert;
  height?: string | number;
}

const SEVERITY_HEX_COLORS: Record<string, string> = {
  minor: '#9e9e9e',
  moderate: '#ff9800',
  severe: '#f44336',
  extreme: '#d32f2f',
};

export const AlertMap: React.FC<AlertMapProps> = ({ alert, height = 300 }) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    let isMounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    setLoading(true);
    setError(null);

    const initMap = () => {
      if (!isMounted || !mapContainerRef.current) return;

      if (!window.google || !window.google.maps) {
        if (++attempts >= 30) {
          setError('Map unavailable. Refresh the page to try again. Your storm details are still available below.');
          setLoading(false);
          return;
        }
        retryTimer = setTimeout(initMap, 500);
        return;
      }

      try {
        const position = { lat: alert.latitude, lng: alert.longitude };

        const map = new google.maps.Map(mapContainerRef.current, {
          center: position,
          zoom: 12,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });

        mapInstanceRef.current = map;

        // Add marker at alert location
        const marker = new google.maps.Marker({
          position,
          map,
          title: `${alert.event_type.toUpperCase()} Alert`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: SEVERITY_HEX_COLORS[alert.severity] || '#f44336',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        });

        markerRef.current = marker;

        // Add impact radius circle (approximate 5-mile impact area for visualization)
        const circle = new google.maps.Circle({
          map,
          center: position,
          radius: 8047, // 5 miles in meters
          fillColor: SEVERITY_HEX_COLORS[alert.severity] || '#f44336',
          fillOpacity: 0.15,
          strokeColor: SEVERITY_HEX_COLORS[alert.severity] || '#f44336',
          strokeOpacity: 0.5,
          strokeWeight: 2,
        });

        circleRef.current = circle;

        // Add info window
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; font-family: system-ui, -apple-system, sans-serif;">
              <strong style="font-size: 14px;">${alert.event_type.toUpperCase()} Alert</strong>
              <br/>
              <span style="color: #666; font-size: 12px;">
                ${alert.city ? `${alert.city}, ` : ''}${alert.state || ''}
                ${alert.zip_code ? ` (${alert.zip_code})` : ''}
              </span>
            </div>
          `,
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        setLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map');
        setLoading(false);
      }
    };

    initMap();

    return () => {
      isMounted = false;
      clearTimeout(retryTimer);
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, [alert]);

  if (error) {
    return (
      <JoyAlert color="danger" sx={{ m: 2 }}>
        {error}
      </JoyAlert>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 'md', overflow: 'hidden' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.level1',
            zIndex: 1,
          }}
        >
          <CircularProgress size="md" />
        </Box>
      )}
      <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default AlertMap;
